import { useEffect, useState } from "react";
import { X, MapPin, Calendar, DollarSign, Tag, Globe, Plus, MessageCircle } from "lucide-react";
import MapView from "@/components/maps/MapView";
import { fmtNumber } from "@/lib/format";
import { travelApi } from "@/lib/travelApi";
import CulturalAssistant from "./CulturalAssistant";

export default function PlaceDetailModal({ place, onClose, onShareExperience }) {
  const [linkedExps, setLinkedExps] = useState([]);

  useEffect(() => {
    if (!place?.id) return;
    travelApi.experiencesByPlace(place.id).then(setLinkedExps).catch(() => setLinkedExps([]));
  }, [place?.id]);

  if (!place) return null;
  const photos = (place.photos || []).filter(Boolean);
  const cover = place.image || photos[0];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="place-modal"
      onClick={onClose}
    >
      <div
        className="bg-cream w-full sm:max-w-3xl max-h-[95vh] sm:max-h-[88vh] rounded-t-3xl sm:rounded-3xl overflow-hidden border hairline shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cover */}
        <div className="relative aspect-[16/9] bg-cream-deep shrink-0">
          {cover && <img src={cover} alt={place.name} className="w-full h-full object-cover" />}
          <button
            onClick={onClose}
            data-testid="place-modal-close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-cream/90 backdrop-blur-sm border hairline grid place-items-center hover:bg-cream"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute bottom-3 left-4 text-cream">
            <p className="font-mono text-[9px] uppercase tracking-widest opacity-90">◊ {place.category}</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight drop-shadow">{place.name}</h2>
            <p className="text-[12px] font-mono inline-flex items-center gap-1 opacity-90">
              <MapPin className="w-3 h-3" /> {place.city ? `${place.city}, ` : ""}{place.country}
            </p>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6">
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {place.cost_min_usd != null && place.cost_max_usd != null && (
              <div className="bg-cream-card border hairline rounded-2xl p-3">
                <p className="font-mono text-[9px] uppercase tracking-widest text-ink-mid mb-0.5 inline-flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Costo/día
                </p>
                <p className="font-medium text-sm">${place.cost_min_usd}–${place.cost_max_usd} USD</p>
              </div>
            )}
            {place.best_season && (
              <div className="bg-cream-card border hairline rounded-2xl p-3">
                <p className="font-mono text-[9px] uppercase tracking-widest text-ink-mid mb-0.5 inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Mejor época
                </p>
                <p className="font-medium text-sm">{place.best_season}</p>
              </div>
            )}
            <div className="bg-cream-card border hairline rounded-2xl p-3">
              <p className="font-mono text-[9px] uppercase tracking-widest text-ink-mid mb-0.5 inline-flex items-center gap-1">
                <Globe className="w-3 h-3" /> Viajeros
              </p>
              <p className="font-medium text-sm">{fmtNumber(place.travelers || 0)}</p>
            </div>
            {place.lat != null && place.lng != null && (
              <div className="bg-cream-card border hairline rounded-2xl p-3">
                <p className="font-mono text-[9px] uppercase tracking-widest text-ink-mid mb-0.5 inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Coordenadas
                </p>
                <p className="font-mono text-[11px]">{place.lat.toFixed(2)}, {place.lng.toFixed(2)}</p>
              </div>
            )}
          </div>

          {/* History */}
          {place.history && (
            <section data-testid="place-history">
              <p className="font-mono text-[10px] uppercase tracking-widest text-coral mb-2">Historia & cultura</p>
              <p className="text-sm leading-relaxed text-ink whitespace-pre-line">{place.history}</p>
            </section>
          )}

          {/* Description */}
          {place.description && (
            <section>
              <p className="font-mono text-[10px] uppercase tracking-widest text-coral mb-2">Por qué ir</p>
              <p className="text-sm text-ink-mid">{place.description}</p>
            </section>
          )}

          {/* Tags */}
          {place.tags && place.tags.length > 0 && (
            <section>
              <p className="font-mono text-[10px] uppercase tracking-widest text-coral mb-2 inline-flex items-center gap-1">
                <Tag className="w-3 h-3" /> Lo que te llevarás
              </p>
              <div className="flex flex-wrap gap-1.5">
                {place.tags.map((t) => (
                  <span key={t} className="text-[11px] font-mono bg-cream-card border hairline rounded-full px-2.5 py-0.5">#{t}</span>
                ))}
              </div>
            </section>
          )}

          {/* Photo gallery */}
          {photos.length > 1 && (
            <section data-testid="place-gallery">
              <p className="font-mono text-[10px] uppercase tracking-widest text-coral mb-2">Galería</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden bg-cream-deep">
                    <img src={p} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Map */}
          {place.lat != null && place.lng != null && (
            <section>
              <p className="font-mono text-[10px] uppercase tracking-widest text-coral mb-2">En el mapa</p>
              <MapView
                items={[{ id: place.id, lat: place.lat, lng: place.lng, title: place.name }]}
                center={[place.lat, place.lng]}
                height="280px"
                testid="place-modal-map"
                renderPopup={(it) => (
                  <div className="text-sm">
                    <p className="font-medium">{place.name}</p>
                    <p className="text-[11px] text-ink-mid">{place.city}, {place.country}</p>
                  </div>
                )}
              />
            </section>
          )}

          {/* Cultural AI Assistant */}
          <CulturalAssistant place={place} />

          {/* Linked community experiences */}
          <section data-testid="place-experiences">
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-coral inline-flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> Experiencias de la comunidad ({linkedExps.length})
              </p>
              {onShareExperience && (
                <button
                  onClick={() => onShareExperience(place)}
                  data-testid="place-share-experience"
                  className="text-xs inline-flex items-center gap-1 bg-coral text-white px-3 py-1.5 rounded-full hover:bg-coral/90 transition"
                >
                  <Plus className="w-3 h-3" /> Compartir la mía
                </button>
              )}
            </div>
            {linkedExps.length === 0 ? (
              <p className="text-sm text-ink-mid bg-cream-card border hairline rounded-2xl p-4 text-center">
                Aún nadie compartió su experiencia en {place.name}. ¿Te animás a ser el primero?
              </p>
            ) : (
              <div className="space-y-2">
                {linkedExps.slice(0, 4).map((e) => (
                  <article key={e.id} data-testid={`place-exp-${e.id}`} className="bg-cream-card border hairline rounded-2xl p-3">
                    <header className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-coral/15 grid place-items-center text-[10px] font-mono text-coral overflow-hidden">
                        {e.user_avatar
                          ? <img src={e.user_avatar} alt="" className="w-full h-full object-cover" />
                          : (e.user_name?.[0]?.toUpperCase() || "V")}
                      </div>
                      <p className="text-[12px] font-medium truncate flex-1">{e.user_name}</p>
                    </header>
                    <h4 className="font-serif text-base leading-tight">{e.title}</h4>
                    {e.body && <p className="text-[12px] text-ink-mid mt-1 line-clamp-2">{e.body}</p>}
                    {e.tips && (
                      <p className="text-[11px] text-coral mt-1.5 inline-flex items-start gap-1">
                        <span className="font-mono text-[9px] uppercase tracking-widest shrink-0 mt-0.5">Tip ▸</span>
                        <span className="text-ink line-clamp-2">{e.tips}</span>
                      </p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
